<template>
	<label :for="_uid" :uploading="uploading" :error="error" :url="url" :class="{ 'block relative border-2 border-transparent': showProgress }" ref="container">
		<button
			type="button"
			v-if="showDelete"
			@click.stop.prevent="
				() => {
					url = null
					$emit('url', null)
				}
			"
			class="absolute -top-1 -right-1 w-[0.9rem] h-[0.9rem] rounded-full border-2 border-black/50 dark:bg-red-900 bg-red-500 flex items-center justify-center text-4xs font-bold z-50"
		>
			<span class=""><i class="fa fa-times opacity-50" /></span>
		</button>

		<slot :progress="progress" />

		<input :id="_uid" hidden type="file" :accept="accept" @change="upload($event.target.files[0])" />

		<Modal :active="img" @hide="cropImage" dialogClasses="!p-0 !border-0" maxWidth="max-w-4xl">
			<Cropper
				ref="cropper"
				class="cropper w-full !p-0"
				@click.stop.prevent
				:src="img"
				:stencil-props="{
					aspectRatio: cropAspect,
				}"
			/>
		</Modal>
	</label>
</template>

<script>
import { heicTo } from 'heic-to'
import Compress from 'compress.js'
const compress = new Compress()
import { apiDomain } from '../../../config/config/settings.json'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import Swal from 'sweetalert2'

export default {
	components: { Cropper },
	props: {
		accept: { type: String, default: 'image/png, image/jpeg, image/heic' },
		showProgress: { type: Boolean, default: true },
		multiple: { type: Boolean, default: true },
		showDelete: { type: Boolean, default: true },
		cropPrompt: { type: Boolean, default: false },
		cropAspect: { type: Number, default: 1 / 1 },
	},
	data() {
		return {
			_uid: Math.random().toString(36).substr(2, 10),
			uploading: false,
			error: false,
			url: null,
			progress: 0,
			filename: null,
			containerSize: 0,
			progressWidth: 2,
			img: null,
			cropped: false,
		}
	},
	watch: {
		uploading() {
			this.$emit('uploading', this.uploading)
		},
		error() {
			this.$emit('error', this.error)
		},
		url() {
			this.$emit('url', this.url)
		},
		filename() {
			this.$emit('filename', this.filename)
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.containerSize = this.$refs.container.offsetWidth
		})
	},
	computed: {
		radius() {
			return this.containerSize / 2 - this.progressWidth / 2 || 0
		},
		progressRingStyle() {
			const circumference = 2 * Math.PI * this.radius
			const offset = circumference - (this.progress / 100) * circumference
			return {
				strokeDasharray: `${circumference} ${circumference}`,
				strokeDashoffset: offset,
			}
		},
	},
	methods: {
		async upload(file, bypass) {
			if (!file) return

			console.log('Uploading file:', file)

			const formData = new FormData()
			this.uploading = true
			this.filename = file?.name || this.filename

			try {
				if (file.type.includes('image') && !bypass) {
					console.log('Image file detected')
					if (file.name.toLowerCase().endsWith('.heic')) {
						file = await heicTo({
							blob: file,
							type: 'image/jpeg',
							quality: 0.75,
						})
						file = new File([file], 'image.jpg', { type: 'image/jpeg' })
					}
					const results = await compress.compress([file], {
						quality: 0.75,
						maxWidth: 1920,
						maxHeight: 1920,
						resize: true,
						rotate: false,
					})
					const img1 = results[0]
					const base64str = img1.data
					const imgExt = img1.ext
					const filename = file.name.replace('.heic', '.jpeg').replace('.HEIC', '.jpeg')
					file = Compress.convertBase64ToFile(base64str, imgExt)
					file = new File([file], filename, { type: file.type })
					if (this.cropPrompt && !this.cropped) {
						this.img = URL.createObjectURL(file)
						return
					}
				}

				formData.append('file', file)

				const xhr = new XMLHttpRequest()
				xhr.open('POST', `${apiDomain}/upload`, true)
				xhr.upload.onprogress = (event) => {
					if (event.lengthComputable) {
						this.progress = (event.loaded / event.total) * 100
					}
				}
				xhr.onload = () => {
					if (xhr.status === 200) {
						const json = JSON.parse(xhr.response)
						const path = json.paths[0]
						const url = apiDomain + path
						this.uploading = false
						this.url = url
						this.progress = 0
					} else {
						this.error = 'Error while uploading file'
						this.uploading = false
					}
				}
				xhr.onerror = () => {
					this.error = 'Error while uploading file'
					this.uploading = false
				}
				xhr.send(formData)
			} catch (error) {
				console.error('Error during upload:', error)
				this.error = 'Error while uploading file'
				this.uploading = false
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Image not supported.',
				})
			}
		},
		async cropImage() {
			try {
				this.img = null
				const { canvas } = this.$refs.cropper.getResult()
				let image = canvas.toDataURL()
				// image = new File([image], this.filename, { type: 'image/jpeg' })
				console.log('Cropped image:', image)
				// return

				// Fetch the blob and create a file
				const response = await fetch(image)
				const blob = await response.blob()
				const file = new File([blob], this.filename, { type: blob.type })

				this.cropped = true
				console.log('Uploading cropped file:', file)
				this.upload(file, true)
			} catch (error) {
				console.error('Error during cropping:', error)
				this.error = 'Error while cropping image'
			}
		},
	},
}
</script>

<style scoped>
.progress-ring__circle {
	transition: stroke-dashoffset 0.35s;
	transform: rotate(-90deg);
	transform-origin: 50% 50%;
}
</style>
